import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { firstValueFrom } from "rxjs";
import { DataService } from "src/app/supabase/services/data.service";
import { GcalendarService } from "./gcalendar.service";
import { LocalCalendarService } from "./localcalendar.service";
import { updateCustomer } from "src/app/auth/ngrx/customer.actions";
import { addCalendarResources } from "src/app/calendar/ngrx/calendar.actions";
import { AppointmentDto } from "src/app/calendar/interfaces/appointment_dto";
import { CalendarResourceDto } from "src/app/calendar/interfaces/calendar_resource_dto";

@Injectable({
  providedIn: 'root'
})
export class CalendarSyncService {
  constructor(
    private store: Store<any>,
    private dataService: DataService,
    private gcalendarService: GcalendarService,
    private localCalendarService: LocalCalendarService
  ) {}

  async syncFutureAppointments(appointments: AppointmentDto[], calendarType: 'google' | 'local', calendarId?: string) {
    const futureAppointments = appointments.filter(
      appointment => new Date(appointment.StartTime) > new Date()
    );

    for (const appointment of futureAppointments) {
      if (calendarType === 'google') {
        await this.syncAppointmentToGoogle(appointment, calendarId);
      } else {
        await this.syncAppointmentToLocal(appointment);
      }
    }
  }

  private async syncAppointmentToGoogle(appointment: AppointmentDto, calendarId: string) {
    const googleEvent = {
      summary: appointment.Subject,
      description: appointment.Description,
      start: {
        dateTime: new Date(appointment.StartTime).toISOString(),
        timeZone: "Europe/Berlin"
      },
      end: {
        dateTime: new Date(appointment.EndTime).toISOString(),
        timeZone: "Europe/Berlin"
      },
      location: appointment.Location
    };

    try {
      await this.gcalendarService.createGoogleEvent(
        calendarId,
        googleEvent
      );
    } catch (error) {
      console.error('Error creating Google calendar event:', error);
    }
  }

  private async syncAppointmentToLocal(appointment: AppointmentDto) {
    const localEvent = {
      title: appointment.Subject,
      location: appointment.Location,
      startDate: new Date(appointment.StartTime).getTime(),
      endDate: new Date(appointment.EndTime).getTime(),
    };

    try {
      await this.localCalendarService.createLocalEvent(localEvent);
    } catch (error) {
      console.error('Error creating local calendar event:', error);
    }
  }

  async setupGoogleCalendar(customer: any, providerToken: string, providerRefreshToken: string) {
    this.gcalendarService.token = providerToken;
    let updatedCustomer;
    let quickDriveCal;
    
    if (!customer.google_calendar_id) {
      quickDriveCal = await this.gcalendarService.createGoogleCalendar("Quick Drive");
      updatedCustomer = {
        ...customer,
        google_refresh_token: providerRefreshToken,
        google_calendar_id: quickDriveCal.id,
      };

      const appointments = await firstValueFrom(this.dataService.getAppointments());
      await this.syncFutureAppointments(appointments, 'google', quickDriveCal.id);
    } else {
      updatedCustomer = {
        ...customer,
        google_refresh_token: providerRefreshToken,
      };
    }

    this.store.dispatch(updateCustomer({ customer: updatedCustomer }));

    const googleCalendars = await this.gcalendarService.getGoogleCalendars();
    const resources = googleCalendars.map((item) => ({
      name: item.summary,
      id: item.id,
      color: item.backgroundColor,
      enabled: true,
      source: "google",
    }));
    
    this.store.dispatch(addCalendarResources({ resources }));
    
    return quickDriveCal?.id;
  }

  async setupLocalCalendar(customer: any) {
    const updatedCustomer = {
      ...customer,
      local_calendar_connected: true
    };

    this.store.dispatch(updateCustomer({ customer: updatedCustomer }));

    const appointments = await firstValueFrom(this.dataService.getAppointments());
    await this.syncFutureAppointments(appointments, 'local');

    const localCalendars = await this.localCalendarService.getLocalCalendars();
    const resources = localCalendars.map((item) => ({
      name: item.title,
      id: item.id,
      color: item.color,
      enabled: true,
      source: "local",
    } as CalendarResourceDto));

    this.store.dispatch(addCalendarResources({ resources }));
  }
} 